import { parseDate } from "@product/scmp-sdk";
import format from "date-fns/format";
import intervalToDuration from "date-fns/intervalToDuration";
import isPast from "date-fns/isPast";
import isToday from "date-fns/isToday";
import groupBy from "lodash/groupBy";
import isNumber from "lodash/isNumber";
import uniq from "lodash/uniq";
import { type FunctionComponent, useCallback, useState } from "react";
import { Navigation } from "swiper/modules";

import { useOlympicScmpWidgetConfig } from "~/components/olympic-widgets/hooks";
import { OlympicTodayHighlightsTabs } from "~/components/olympic-widgets/today-highlights/today-highlights-tabs";
import { formatInTimeZone } from "~/lib/utils";
import type { hooksOlympicWidgetsQuery$key } from "~/queries/__generated__/hooksOlympicWidgetsQuery.graphql";

import { useOlympicEvents } from "./hooks";
import {
  Container,
  DayText,
  Header,
  StyledArrowNext,
  StyledArrowPrevious,
  StyledSwiper,
  StyledSwiperSlide,
  SwiperContainer,
} from "./styles";
import type { CombinedOlympicEvents, OlympicEvents } from "./types";

type Props = {
  className?: string;
  reference?: hooksOlympicWidgetsQuery$key;
};

export const OlympicTodayHighlights: FunctionComponent<Props> = ({ className, reference }) => {
  const { data } = useOlympicEvents();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isInit, setIsInit] = useState(false);
  const appConfig = useOlympicScmpWidgetConfig(reference);
  const olympicsStartDay = appConfig?.olympics_today_highlight_day_1 ?? "2024-07-27";

  const combinedList = useCallback(() => {
    const dates: string[] = [];
    const combined: CombinedOlympicEvents = {};
    const groupedByDate = (list: OlympicEvents[]) =>
      groupBy(list, item => {
        dates.push(item.date);
        return item.date;
      });

    const groupedChina = groupedByDate(data.china);
    const groupedHk = groupedByDate(data.hk);
    const uniqDates = uniq(dates);
    uniqDates.forEach(date => {
      combined[date] = {
        china: groupedChina[date] ?? [],
        hk: groupedHk[date] ?? [],
      };
    });

    const findClosestDateIndex = () => {
      const currentDate = formatInTimeZone(new Date(), "yyyyMMdd");
      const datesAsInt = uniqDates.map(date =>
        isPast(parseDate(`${date}`)) && !isToday(parseDate(`${date}`)) ? -1 : Number.parseInt(date),
      );
      const currentDateInt = Number.parseInt(currentDate);

      const closestDate = datesAsInt.reduce((closest, date) => {
        const diff = Math.abs(date - currentDateInt);
        if (diff < Math.abs(closest - currentDateInt)) {
          return date;
        }
        return closest;
      });
      const closetDateIndex = uniqDates.indexOf(closestDate.toString());

      return closetDateIndex === -1 ? uniqDates.length - 1 : closetDateIndex;
    };

    return { closetDateIndex: findClosestDateIndex(), dates: uniqDates, lists: combined };
  }, [data.china, data.hk]);

  if (data.china.length === 0 && data.hk.length === 0) return null;

  const renderSlide = () =>
    combinedList().dates.map(date => {
      const dateFormat = format(parseDate(date), "MMM dd, yyyy");
      const day = intervalToDuration({
        end: parseDate(date),
        start: new Date(formatInTimeZone(new Date(olympicsStartDay), "MMM dd, yyyy")),
      });
      const displayDay = isNumber(day.days) ? day.days : 0;

      return (
        <StyledSwiperSlide key={date}>
          <DayText>
            Day {displayDay + 1} | {dateFormat}
          </DayText>
        </StyledSwiperSlide>
      );
    });

  const closetDateIndex = combinedList().closetDateIndex;
  const currentDate = combinedList().dates[currentSlide];
  const currentTabData = combinedList().lists[currentDate];

  return (
    <Container className={className}>
      <Header>Today&apos;s highlights</Header>
      <SwiperContainer>
        <StyledSwiper
          modules={[Navigation]}
          navigation={{
            disabledClass: "today-highlights-swiper-button-disabled",
            nextEl: ".today-highlights-swiper-button-next",
            prevEl: ".today-highlights-swiper-button-prev",
          }}
          pagination={false}
          onSlideChange={swiper => {
            const currentSlide = swiper.activeIndex;
            setCurrentSlide(currentSlide);
          }}
          onSwiper={swiper => {
            swiper.slideTo(closetDateIndex, 0, false);
            setIsInit(true);
          }}
        >
          {renderSlide()}
          <div className="swiper-button today-highlights-swiper-button-prev">
            <StyledArrowPrevious />
          </div>
          <div className="swiper-button today-highlights-swiper-button-next">
            <StyledArrowNext />
          </div>
        </StyledSwiper>
      </SwiperContainer>
      {isInit && <OlympicTodayHighlightsTabs tabData={currentTabData} />}
    </Container>
  );
};

OlympicTodayHighlights.displayName = "OlympicTodayHighlights";
